import "../MuseoSansBinary.js"
import { parseTime } from "../utils/helperFunctions"
import { createLetterPdf } from "../pdfUtils"

function formatClubAbbrev(raw) {
  if (!raw) return ""
  const trimmed = String(raw).trim()
  if (!trimmed) return ""
  return trimmed.length > 4 ? trimmed.slice(0, 4) : trimmed
}

function formatFileName(meetName, meetDate) {
  const cleanName = (meetName || "Lineup").replace(/[^a-zA-Z0-9]+/g, "_")
  const cleanDate = (meetDate || "").replace(/[^0-9-]+/g, "")
  if (cleanDate) {
    return `Lineup_${cleanName}_${cleanDate}.pdf`
  }
  return `Lineup_${cleanName}.pdf`
}

function formatGenderLabel(eventGender) {
  if (eventGender === "Girls" || eventGender === "Boys") return eventGender
  if (eventGender === 1 || eventGender === "1") return "Girls"
  if (eventGender === 2 || eventGender === "2") return "Boys"
  return "All"
}

function resolveEvNum(displayNumber, eventNumb) {
  const raw = eventNumb || ""
  return (displayNumber && displayNumber !== String(raw)) ? displayNumber : raw
}

function formatSwimmerDisplayName(name) {
  if (!name) return ""
  const trimmed = String(name).trim()
  const commaIndex = trimmed.indexOf(",")
  if (commaIndex > 0) {
    const last = trimmed.slice(0, commaIndex).trim()
    const first = trimmed.slice(commaIndex + 1).trim()
    if (first && last) {
      return `${first} ${last}`
    }
  }
  return trimmed
}

function getRelayLegLabel(teamPosition) {
  if (teamPosition === null || teamPosition === undefined) return ""
  const num = parseInt(String(teamPosition), 10)
  if (Number.isNaN(num)) return ""
  if (num === 1) return "1st"
  if (num === 2) return "2nd"
  if (num === 3) return "3rd"
  if (num === 4) return "4th"
  return `${num}th`
}

function getRelayLegDisplay(teamPosition, stroke) {
  const base = getRelayLegLabel(teamPosition)
  if (!base) return ""

  const strokeText = (stroke || "").toLowerCase()
  const isMedley = strokeText.includes("medley")
  if (!isMedley) {
    return base
  }

  const num = parseInt(String(teamPosition), 10)
  let legStroke = ""
  if (num === 1) legStroke = "Back"
  else if (num === 2) legStroke = "Breast"
  else if (num === 3) legStroke = "Fly"
  else if (num === 4) legStroke = "Free"

  return legStroke ? `${base}, ${legStroke}` : base
}

function getAgeGroupSortKey(ageGroup) {
  if (!ageGroup || typeof ageGroup !== "string") return Number.MAX_SAFE_INTEGER
  const match = ageGroup.match(/\d+/)
  if (!match) return Number.MAX_SAFE_INTEGER
  return parseInt(match[0], 10)
}

/**
 * Parse a human-readable age-group label into a [min, max] integer range.
 * Mirrors the parseAgeGroup helper in reportingController.js.
 *   "7-8"        → [7, 8]
 *   "8 & Under"  → [0, 8]
 *   "13 & Over"  → [13, 999]
 *   "Open"       → [0, 999]
 */
function parseAgeGroupRange(label) {
  if (!label || typeof label !== "string") return [0, 999]
  const s = label.trim().toLowerCase()
  const underMatch = s.match(/^(\d+)\s*(?:&|and)\s*under$/)
  if (underMatch) return [0, parseInt(underMatch[1], 10)]
  const overMatch = s.match(/^(\d+)\s*(?:&|and)\s*over$/)
  if (overMatch) return [parseInt(overMatch[1], 10), 999]
  const rangeMatch = s.match(/^(\d+)\s*-\s*(\d+)$/)
  if (rangeMatch) return [parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10)]
  if (s === "open") return [0, 999]
  const num = parseInt(s, 10)
  if (!isNaN(num)) return [num, num]
  return [0, 999]
}

function formatEntryLine(rec, includeTimes) {
  const genderLabel = formatGenderLabel(rec.event_gender)
  const distance = rec.distance || ""
  const course = rec.course || ""
  const stroke = rec.stroke || ""
  const ageGroup = rec.age_group || ""
  const eventNumber = resolveEvNum(rec.display_number, rec.event_numb)
  const divSuffix = rec.division ? ` [${rec.division}]` : ""
  const exPrefix = rec.is_exhibition ? "Ex. " : ""
  // Format event numbers as two digits (01, 02, 12, etc.) so the
  // "tens place" of the number aligns vertically across rows.
  const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
  const eventNumberFormatted = eventNumberStr
    ? eventNumberStr.padStart(2, "0")
    : ""

  let base = `${exPrefix}Event ${eventNumberFormatted}${divSuffix}: ${ageGroup} ${genderLabel} ${distance}${course} ${stroke}`.trim()

  const heatLane = `Heat: ${rec.heat || "?"}  Lane: ${rec.lane || "?"}`

  let timePart = ""
  if (includeTimes) {
    if (rec.entry_time_display) {
      timePart = `  Entry: ${rec.entry_time_display}`
    } else {
      timePart = "  Entry: NT"
    }
  }

  let legPart = ""
  if (rec.is_relay === 1) {
    const legDisplay = getRelayLegDisplay(rec.team_position, rec.stroke)
    if (legDisplay) {
      legPart = `  Leg: ${legDisplay}`
    }
  }

  return `${base}   ${heatLane}${timePart}${legPart}`
}

function computeRequiredHeightForSwimmer(
  recObj,
  includeNotes,
  includeHeatLane,
  doc,
  columnWidth,
  includeTimes,
  isPsychSheetThreeColumn,
) {
  const baseLines = 2 // name line + entries summary
  const lineHeight = 5

  // With the updated layout, each entry can use up to two lines
  // whether or not heats/lanes are shown (event line + details line).
  const perEntryLines = 2
  const entryLines = recObj.records.length * perEntryLines

  const notesLines = includeNotes ? 2 : 0
  const totalLines = baseLines + entryLines + notesLines
  return totalLines * lineHeight + 4
}

function getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight) {
  if (!imageWidth || !imageHeight) {
    return { width: maxWidth, height: maxHeight }
  }

  const widthRatio = maxWidth / imageWidth
  const heightRatio = maxHeight / imageHeight
  const scale = Math.min(widthRatio, heightRatio, 1)

  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
  }
}

function drawReportLogo(doc, logoDataUri, imageWidth, imageHeight, x, y, maxWidth, maxHeight) {
  if (!logoDataUri) return

  try {
    const size = getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight)
    const drawX = x + (maxWidth - size.width) / 2
    const drawY = y + (maxHeight - size.height) / 2
    doc.addImage(logoDataUri, drawX, drawY, size.width, size.height)
  } catch (_err) {
    // Ignore logo rendering failures so report formatting remains stable.
  }
}

export default function generateLineupPDF(
  data,
  options,
  meta,
) {
  const { recordsAggregated, meetName, meetDate } = data
  const {
    columns,
    includeTimes,
    includeNotes,
    selectedAgeGroups,
    genderFilter,
    clubName,
    onlyRelays,
    openOnlyMeet,
    viewMode = "ageGroup",
    includeHeatLane = true,
    multiClub = false,
    meetPsychSheet = false,
    logoDataUri = null,
    logoWidth = null,
    logoHeight = null,
  } = options

  const relaysOnly = !!onlyRelays

  const doc = createLetterPdf()
  const margin = 10
  const pageWidth = doc.internal.pageSize.getWidth() - margin * 2
  const columnWidth = (pageWidth - (columns - 1) * 5) / columns
  const isPsychSheetThreeColumn = !includeHeatLane && columns === 3
  const isThreeColumnLayout = columns === 3

  // Per-column typography scaling so 1- and 2-column layouts
  // can use slightly larger type while 3-column stays compact.
  const swimmerNameFontSize = columns === 1 ? 12 : 11
  const summaryFontSize = isThreeColumnLayout ? 7 : columns === 1 ? 9 : 8
  const swimmerRowFontSize = Math.max(summaryFontSize - 1, 5)
  const notesLabelFontSize = columns === 1 ? 8 : 7
  let currentColumn = 0
  let yPos = 30

  const drawHeader = () => {
    // All text in this report uses the embedded Museo Sans face
    doc.setFont("MuseoSansRounded-500", "normal")
    doc.setFontSize(12)

    const title = meetName || "Lineup Report"
    const titleWidth = doc.getTextWidth(title)
    const titleX = margin + (pageWidth - titleWidth) / 2
    doc.text(title, titleX, 14)

    if (meetDate) {
      const dateText = String(meetDate).replace(/\//g, "-")
      const dateWidth = doc.getTextWidth(dateText)
      const dateX = margin + (pageWidth - dateWidth) / 2
      doc.text(dateText, dateX, 20)
    }

    doc.setFontSize(9)
    drawReportLogo(
      doc,
      logoDataUri,
      logoWidth,
      logoHeight,
      margin + pageWidth - 28,
      6,
      28,
      14,
    )

    const isEventPsychSheet = viewMode === "event" && !includeHeatLane
    const isMeetPsychSheetHeader = isEventPsychSheet && meetPsychSheet

    const clubLine = isMeetPsychSheetHeader
      ? "Meet Psych Sheet"
      : clubName
      ? relaysOnly
        ? `Lineup Report, Relays Only for ${clubName}`
        : `Lineup Report for ${clubName}`
      : relaysOnly
      ? "Lineup Report, Relays Only"
      : "Lineup Report"
    const clubWidth = doc.getTextWidth(clubLine)
    const clubX = margin + (pageWidth - clubWidth) / 2
    doc.text(clubLine, clubX, 26)

    const summaryPrefix = isMeetPsychSheetHeader ? "Meet Summary" : "Team Summary"
    // Always show Individual, Relay, and Exhibition entry counts on
    // this summary line for every permutation of the lineup report.
    const summaryLine = `${summaryPrefix}  Ind: ${teamIndividual}  Relay: ${teamRelay}  Ex. Ind: ${teamExhibition}  Ex. Relay: ${teamExhibitionRelay}`
    const summaryWidth = doc.getTextWidth(summaryLine)
    const summaryX = margin + (pageWidth - summaryWidth) / 2
    doc.text(summaryLine, summaryX, 32)

    doc.setFontSize(notesLabelFontSize)
    doc.text("Powered by Swimmingly®", margin, 8)

    // Vertical column dividers between columns for multi-column
    // layouts, spanning the body content area.
    if (columns > 1) {
      doc.setDrawColor(0)
      doc.setLineWidth(0.1)
      const bodyTopY = 40
      const bodyBottomY = 280
      for (let i = 1; i < columns; i += 1) {
        const x = margin + i * columnWidth + (i - 1) * 5 + 2.5
        doc.line(x, bodyTopY, x, bodyBottomY)
      }
    }
  }

  const checkPageHeight = (requiredSpace) => {
    if (yPos + requiredSpace <= 280) return
    // Start new column at the same vertical offset as the
    // first content under the header so first swimmer rows
    // align across columns.
    yPos = 40
    currentColumn++
    if (currentColumn >= columns) {
      currentColumn = 0
      doc.addPage()
      drawHeader()
      yPos = 40
    }
  }

  // Filter swimmers by selected age groups (age_group only) and sex
  const selectedAgeGroupSet = new Set(selectedAgeGroups || [])

  const swimmersWithBuckets = recordsAggregated.map((swimmer) => {
    const buckets = new Set()
    const primaryRecord = swimmer.records[0] || {}
    const swimmerAge = parseInt(String(primaryRecord.age || 0), 10)

    // Collect the distinct gender labels present in this swimmer's records.
    const genderLabels = new Set()
    swimmer.records.forEach((rec) => {
      if (relaysOnly && rec.is_relay !== 1) return
      genderLabels.add(formatGenderLabel(rec.event_gender))
    })

    // Assign this swimmer to every selected age-group section whose numeric
    // range includes their actual league age. This correctly handles
    // overlapping age groups (e.g. "7-8" and "8 & Under") and swim-ups:
    // all of the swimmer's events are shown under whichever sections they
    // qualify for, regardless of the individual event's own age_group label.
    selectedAgeGroupSet.forEach((ageGroupLabel) => {
      const [min, max] = parseAgeGroupRange(ageGroupLabel)
      if (swimmerAge >= min && swimmerAge <= max) {
        genderLabels.forEach((genderLabel) => {
          buckets.add(`${ageGroupLabel}:::${genderLabel}`)
        })
      }
    })

    return { ...swimmer, buckets: Array.from(buckets) }
  })

  // Compute global team summary based on filtered swimmers.
  // For relays, we care about relay TEAMS, not swimmers in the relay,
  // so we count distinct (event_numb, heat, lane) combinations.
  // For individual and exhibition entries, we de-duplicate by a
  // stable swim key so the same swim is never counted twice even if
  // it appears in overlapping reporting buckets.
  let teamIndividual = 0
  let teamRelay = 0
  let teamExhibition = 0
  let teamExhibitionRelay = 0

  const globalRelayTeams = new Set()
  const globalExhibitionRelayTeams = new Set()
  const globalIndividualSwims = new Set()
  const globalExhibitionSwims = new Set()

  swimmersWithBuckets.forEach((swimmer) => {
    // Only count swims for swimmers who appear in at least one bucket
    // (i.e. their league age matches at least one selected age group).
    if (!swimmer.buckets.length) return
    const filteredRecords = swimmer.records.filter((rec) => {
      if (relaysOnly && rec.is_relay !== 1) return false
      return true
    })

    filteredRecords.forEach((r) => {
      // Build a key that uniquely identifies this swimmer's swim.
      const baseKey = `${r.swim_numb || ""}|${r.event_id || ""}|${r.event_numb || ""}|${r.heat || ""}|${
        r.lane || ""
      }`

      if (r.is_relay === 1) {
        const relayKey = `${r.event_numb || ""}|${r.heat || ""}|${r.lane || ""}`
        if (r.is_exhibition) {
          globalExhibitionRelayTeams.add(relayKey)
        } else {
          globalRelayTeams.add(relayKey)
        }
      } else if (r.is_exhibition) {
        globalExhibitionSwims.add(baseKey)
      } else {
        globalIndividualSwims.add(baseKey)
      }
    })
  })

  teamIndividual = globalIndividualSwims.size
  teamExhibition = globalExhibitionSwims.size
  teamRelay = globalRelayTeams.size
  teamExhibitionRelay = globalExhibitionRelayTeams.size

  // For the By Swimmer view, recompute team totals without age-group
  // filtering since that dimension is not used in this layout.
  if (viewMode === "swimmer") {
    const swimmerRelayTeams = new Set()
    const swimmerExhibitionRelayTeams = new Set()
    const swimmerIndividualSwims = new Set()
    const swimmerExhibitionSwims = new Set()
    recordsAggregated.forEach((swimmer) => {
      swimmer.records.forEach((rec) => {
        if (relaysOnly && rec.is_relay !== 1) return
        const baseKey = `${rec.swim_numb || ""}|${rec.event_id || ""}|${rec.event_numb || ""}|${rec.heat || ""}|${rec.lane || ""}`
        if (rec.is_relay === 1) {
          const relayKey = `${rec.event_numb || ""}|${rec.heat || ""}|${rec.lane || ""}`
          if (rec.is_exhibition) {
            swimmerExhibitionRelayTeams.add(relayKey)
          } else {
            swimmerRelayTeams.add(relayKey)
          }
        } else if (rec.is_exhibition) {
          swimmerExhibitionSwims.add(baseKey)
        } else {
          swimmerIndividualSwims.add(baseKey)
        }
      })
    })
    teamIndividual = swimmerIndividualSwims.size
    teamExhibition = swimmerExhibitionSwims.size
    teamRelay = swimmerRelayTeams.size
    teamExhibitionRelay = swimmerExhibitionRelayTeams.size
  }

  drawHeader()
  // Add a bit more space between the header summary line
  // and the first row of body content.
  yPos = 40
  if (viewMode === "event") {
    // By Event view: group entries by event number and list swimmers
    // seeded by entry time (fastest to slowest), without relying on
    // heat/lane assignments which are not yet finalized.
    const allEntries = []
    recordsAggregated.forEach((swimmer) => {
      const primaryRecord = swimmer.records[0] || {}
      const swimmerNameRaw = primaryRecord.name || ""
      const swimmerName = formatSwimmerDisplayName(swimmerNameRaw)
      const swimmerAge = primaryRecord.age || ""
      const rawSwimmerNumber =
        primaryRecord.swim_numb !== undefined && primaryRecord.swim_numb !== null
          ? String(primaryRecord.swim_numb)
          : ""
      const swimmerNumber = rawSwimmerNumber
        ? rawSwimmerNumber.padStart(3, "0")
        : ""

      swimmer.records.forEach((rec) => {
        const ageGroup = rec.age_group || ""
        if (selectedAgeGroupSet.size && !selectedAgeGroupSet.has(ageGroup)) return
        if (relaysOnly && rec.is_relay !== 1) return
        allEntries.push({
          swimmerName,
          swimmerAge,
          swimmerNumber,
          rec,
        })
      })
    })

    if (allEntries.length) {
      const eventsMap = new Map()
      allEntries.forEach((item) => {
        const eventNum = item.rec.event_numb || 0
        const key = String(eventNum)
        if (!eventsMap.has(key)) {
          eventsMap.set(key, [])
        }
        eventsMap.get(key).push(item)
      })

      const sortedEventKeys = Array.from(eventsMap.keys()).sort(
        (a, b) => parseInt(a, 10) - parseInt(b, 10),
      )

      sortedEventKeys.forEach((eventKey, idx) => {
        const entries = eventsMap.get(eventKey) || []
        if (!entries.length) return

        // No heat/lane-based ordering here; we seed swimmers by
        // entry time downstream per event.

        const sampleRec = entries[0].rec
        const genderLabel = formatGenderLabel(sampleRec.event_gender)
        const distance = sampleRec.distance || ""
        const course = sampleRec.course || ""
        const stroke = sampleRec.stroke || ""
        const ageGroup = sampleRec.age_group || ""
        const eventNumber = resolveEvNum(sampleRec.display_number, sampleRec.event_numb)
        const divSuffix = sampleRec.division ? ` [${sampleRec.division}]` : ""
        const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
        const eventNumberFormatted = eventNumberStr
          ? eventNumberStr.padStart(2, "0")
          : ""
        const isRelayEvent = sampleRec.is_relay === 1

        // Group entries within this event by heat, aggregating relay
        // swimmers into a single relay team per event/heat/lane so we
        // can accurately estimate the height needed for heats that are
        // relay-heavy vs individual-heavy.
        const individuals = []
        const relayTeams = new Map()

        entries.forEach((item) => {
          const rec = item.rec
          const heatNum = rec.heat || 0
          const laneNum = rec.lane || 0

          if (rec.is_relay === 1) {
            const teamKey = `${rec.event_numb || ""}|${heatNum}|${laneNum}`
            let team = relayTeams.get(teamKey)
            if (!team) {
              team = {
                eventRec: rec,
                heat: heatNum,
                lane: laneNum,
                legs: [],
              }
              relayTeams.set(teamKey, team)
            }
            team.legs.push({
              team_position: rec.team_position,
              name: item.swimmerName,
              age: item.swimmerAge,
              swimmerNumber: item.swimmerNumber,
            })
          } else {
            individuals.push({
              ...item,
              heat: heatNum,
              lane: laneNum,
            })
          }
        })

        const heatsMap = new Map()

        // For seeding, we no longer rely on heat assignments; we
        // will render a single seeded list per event, but we still
        // reuse the heatsMap structure for estimating height and
        // keeping relay teams grouped logically by original heat.

        individuals.forEach((item) => {
          const key = String(item.heat || 0)
          if (!heatsMap.has(key)) heatsMap.set(key, [])
          heatsMap.get(key).push({ type: "individual", item })
        })

        relayTeams.forEach((team) => {
          const key = String(team.heat || 0)
          if (!heatsMap.has(key)) heatsMap.set(key, [])
          heatsMap.get(key).push({ type: "relayTeam", team })
        })

        const heatKeys = Array.from(heatsMap.keys()).sort(
          (a, b) => parseInt(a, 10) - parseInt(b, 10),
        )
        const totalHeats = heatKeys.length

        const estimateHeatHeight = (heatEntriesForEstimate) => {
          const heatHeaderHeight = columns === 3 ? 3 : 4
          const gapAfterHeat = columns === 3 ? 2 : 3
          let estimated = heatHeaderHeight

          heatEntriesForEstimate.forEach((entry) => {
            if (entry.type === "relayTeam") {
              const summaryHeight = columns === 3 ? 4 : 5
              const legsCount =
                (entry.team.legs && entry.team.legs.length) || 4
              const legsHeight = legsCount * 4
              const extraGap = 2
              estimated += summaryHeight + legsHeight + extraGap
            } else {
              const swimmerRowHeight = columns === 3 ? 4 : 5
              estimated += swimmerRowHeight
            }
          })

          estimated += gapAfterHeat
          return estimated
        }

        // Before we draw the event header, estimate how much space is
        // needed for the header *and* the first heat so the header is
        // never orphaned at the bottom of a column without at least
        // the first heat's content following it.
        const eventHeaderHeight = 12
        const spaceBeforeHeats = columns === 3 ? 5 : 6
        const firstHeatBlockHeight =
          heatKeys.length && heatsMap.get(heatKeys[0])
            ? estimateHeatHeight(heatsMap.get(heatKeys[0]))
            : 0

        const requiredHeightForEventStart =
          eventHeaderHeight + spaceBeforeHeats + firstHeatBlockHeight

        // Ensure that when there isn't enough room left in the
        // current column for the event header plus the first heat,
        // we move cleanly to the next column or, if already in the
        // last column, to a new page starting at the top of the
        // first column. This avoids partially reserving space in one
        // column but drawing the event in another.
        if (yPos + requiredHeightForEventStart > 280) {
          if (currentColumn < columns - 1) {
            // Advance to the next column on the same page.
            currentColumn += 1
            yPos = 40
          } else {
            // Already in the last column: start a fresh page at the
            // top of the first column.
            currentColumn = 0
            doc.addPage()
            drawHeader()
            yPos = 40
          }
        }

        const columnStartX = margin + currentColumn * (columnWidth + 5)

        // Column offsets within a column for lane/name/age/(club)/ID/time.
        // We use offsets so that when heats advance to a new
        // column/page, we can recompute absolute X positions based
        // on the current column without drifting.
        const laneOffset = 6

        // Anchor the Time column at the right edge of the column and
        // work leftwards for ID, Club, and Age so those columns can
        // never overlap each other, regardless of column width.
        // Time column: right-justify header and values against the
        // right edge of this column for individual events.
        const timeOffset = columnWidth - 1
        let numberOffset
        let ageOffset
        let clubOffset = null
        if (includeHeatLane) {
          // Original lane/name/age/ID/time layout.
          numberOffset = timeOffset - 11
          ageOffset = columns === 1 ? numberOffset - 8 : numberOffset - 6
        } else {
          // Psych-sheet layout: insert a Club column between Age and ID.
          numberOffset = timeOffset - 11 // ID
          clubOffset = numberOffset - 9
          ageOffset = columns === 1 ? clubOffset - 8 : clubOffset - 6
        }

        // Event header styled to mirror heat sheet event line
        const eventTitle = `#${eventNumberFormatted}${divSuffix} ${genderLabel} ${ageGroup} ${distance}${course} ${stroke}`.trim()
        if (columns === 3) {
          doc.setFontSize(7)
        } else {
          doc.setFontSize(10)
        }
        doc.setFont("MuseoSansRounded-700", "normal")
        doc.text(eventTitle, columnStartX + 2, yPos)

        // Column header row for the lane/name/age/ID/time columns,
        // with lines above and below and bold weight matching the
        // event header.
        const headerTopY = yPos + 2
        const headerTextY = headerTopY + (columns === 3 ? 2 : 3)
        const headerBottomY = headerTextY + 1

        doc.setLineWidth(0.2)
        doc.setDrawColor(0)
        // Header borders span the full width of the column.
        doc.line(columnStartX, headerTopY, columnStartX + columnWidth, headerTopY)

        doc.setFontSize(summaryFontSize)
        doc.setFont("MuseoSansRounded-700", "normal")

        // Place the Name column so there are visually about two
        // spaces between the right-aligned lane number and the first
        // character of the swimmer name.
        const twoSpaceWidth = doc.getTextWidth("  ")
        const nameOffset = laneOffset + twoSpaceWidth

        // Absolute X positions for this first column of the event.
        const laneXHeader = columnStartX + laneOffset
        const nameXHeader = columnStartX + nameOffset
        const ageXHeader = columnStartX + ageOffset
        const clubXHeader =
          clubOffset !== null ? columnStartX + clubOffset : null
        const numberXHeader = columnStartX + numberOffset
        const timeXHeader = columnStartX + timeOffset

        // Right-justify headers over their aligned columns so the
        // lane numbers or seed indices sit under the last character
        // of the header text, and numeric columns are right-aligned
        // with their data. In Editing Entries (psych-sheet) context,
        // the leftmost column represents seed place, not lane.
        const laneHeader = includeHeatLane ? "Lane" : "Seed"
        const laneHeaderWidth = doc.getTextWidth(laneHeader)
        doc.text(laneHeader, laneXHeader - laneHeaderWidth, headerTextY)

        doc.text("Name", nameXHeader, headerTextY)

        const ageHeader = "Age"
        const ageHeaderWidth = doc.getTextWidth(ageHeader)
        if (isRelayEvent && clubXHeader !== null) {
          // Relay events: move the Age column twelve spaces to the
          // right of the base Age position (an additional six
          // spaces beyond the previous offset) and left-justify the
          // Age header at that X. Club header label is omitted.
          const spaceWidth = doc.getTextWidth(" ")
          const relayAgeHeaderX = ageXHeader + spaceWidth * 12
          doc.text(ageHeader, relayAgeHeaderX, headerTextY)
        } else {
          // Non-relay events: standard Lane, Name, Age, Club, ID, Time.
          doc.text(ageHeader, ageXHeader - ageHeaderWidth, headerTextY)
          if (clubXHeader !== null) {
            const clubHeader = "Club"
            // Left-justify the Club header so it aligns with the
            // left-justified Club values in this column.
            doc.text(clubHeader, clubXHeader, headerTextY)
          }
        }

        const idHeader = "ID"
        const idHeaderWidth = doc.getTextWidth(idHeader)
        doc.text(idHeader, numberXHeader - idHeaderWidth, headerTextY)

        if (includeTimes) {
          const timeHeader = "Time"
          const timeHeaderWidth = doc.getTextWidth(timeHeader)
          doc.text(timeHeader, timeXHeader - timeHeaderWidth, headerTextY)
        }

        doc.line(columnStartX, headerBottomY, columnStartX + columnWidth, headerBottomY)

        // Space before heats within this event
        yPos = headerBottomY + (columns === 3 ? 2 : 3)
        doc.setFontSize(summaryFontSize)
        doc.setFont("MuseoSansRounded-500", "normal")

        if (includeHeatLane) {
          // Heat sheet context: render by heat and lane using the
          // original assignments. Sorting is strictly by heat then
          // lane, and the Lane column shows the actual lane.
          heatKeys.forEach((heatKey, heatIndex) => {
            const heatEntries = heatsMap.get(heatKey) || []
            if (!heatEntries.length) return

            const estimatedHeatHeight = estimateHeatHeight(heatEntries)

            // For the first heat of an event, we already reserved
            // space together with the event header. Subsequent heats
            // still use per-heat pagination.
            if (heatIndex > 0) {
              checkPageHeight(estimatedHeatHeight)
            }

            // Compute absolute X positions for this heat based on the
            // (potentially updated) currentColumn, so heats that break
            // across columns/pages render in the correct column.
            const columnStartXForHeat =
              margin + currentColumn * (columnWidth + 5)
            const laneX = columnStartXForHeat + laneOffset
            const nameX = columnStartXForHeat + nameOffset
            const ageX = columnStartXForHeat + ageOffset
            const clubX =
              clubOffset !== null ? columnStartXForHeat + clubOffset : null
            const numberX = columnStartXForHeat + numberOffset
            const timeX = columnStartXForHeat + timeOffset
            const spaceWidthRelay = doc.getTextWidth(" ")
            const relayAgeX =
              isRelayEvent && clubX !== null
                ? ageX + spaceWidthRelay * 12
                : ageX

            // Heat header similar to the heat sheet.
            const heatNumber = parseInt(heatKey, 10) || 0
            if (columns === 3) {
              doc.setFontSize(7)
            } else {
              doc.setFontSize(10)
            }
            doc.setFont("MuseoSansRounded-700", "normal")
            doc.text(
              `Heat ${heatNumber} of ${totalHeats}`,
              columnStartXForHeat + 2,
              yPos,
            )
            yPos += columns === 3 ? 3 : 4

            // Sort by lane within heat.
            heatEntries.sort((a, b) => {
              const la =
                a.type === "relayTeam" ? a.team.lane || 0 : a.item.lane || 0
              const lb =
                b.type === "relayTeam" ? b.team.lane || 0 : b.item.lane || 0
              return la - lb
            })

            heatEntries.forEach((entry) => {
              if (entry.type === "relayTeam") {
                const team = entry.team
                const baseRec = team.eventRec
                const laneStr =
                  team.lane !== null && team.lane !== undefined
                    ? String(team.lane)
                    : "?"

                let timeText = ""
                if (includeTimes) {
                  if (baseRec.entry_time_display) {
                    timeText = baseRec.entry_time_display
                  } else {
                    timeText = "NT"
                  }
                }

                const teamClubAbbrev = formatClubAbbrev(baseRec.club_name)
                const exRelaySuffix = baseRec.is_exhibition ? " ex." : ""
                const relayLabel = teamClubAbbrev
                  ? `${teamClubAbbrev} Relay${exRelaySuffix}`
                  : `Relay${exRelaySuffix}`

                // Relay team summary row uses the main summary size
                // so it stands out from the swimmer rows below.
                doc.setFontSize(summaryFontSize)
                doc.setFont("MuseoSansRounded-500", "normal")
                const laneWidth = doc.getTextWidth(laneStr)
                doc.text(laneStr, laneX - laneWidth, yPos)
                if (relayLabel) {
                  doc.text(relayLabel, nameX, yPos)
                }
                if (clubX !== null && teamClubAbbrev) {
                  doc.text(teamClubAbbrev, clubX, yPos)
                }
                if (timeText) {
                  const timeWidth = doc.getTextWidth(timeText)
                  doc.text(timeText, timeX - timeWidth, yPos)
                }
                yPos += columns === 3 ? 4 : 5

                const legsSorted = team.legs
                  .slice()
                  .sort(
                    (a, b) =>
                      (a.team_position || 0) - (b.team_position || 0),
                  )

                // Swimmer rows (relay legs): slightly smaller font.
                doc.setFontSize(swimmerRowFontSize)

                legsSorted.forEach((leg) => {
                  const legLabel = getRelayLegDisplay(
                    leg.team_position,
                    baseRec.stroke,
                  )
                  const legLine = `${legLabel}: ${leg.name}`
                  const ageText = leg.age ? String(leg.age) : ""
                  const numberText = leg.swimmerNumber || ""

                  doc.text(legLine, nameX, yPos)
                  if (ageText) {
                    // Relay Age values are left-justified under the
                    // shifted Age header position.
                    doc.text(ageText, relayAgeX, yPos)
                  }
                  if (numberText) {
                    // First digit of ID aligns under the "I" in "ID",
                    // then shift one space to the right for relay legs.
                    const idHeaderWidth = doc.getTextWidth("ID")
                    const spaceWidth = doc.getTextWidth(" ")
                    const idAnchorX = numberX - idHeaderWidth + spaceWidth
                    doc.text(numberText, idAnchorX, yPos)
                  }
                  yPos += 4
                })

                yPos += 2
              } else {
                const { swimmerName, swimmerAge, swimmerNumber, rec } =
                  entry.item
                const laneStr =
                  rec.lane !== null && rec.lane !== undefined
                    ? String(rec.lane)
                    : "?"

                let timeText = ""
                if (includeTimes) {
                  if (rec.entry_time_display) {
                    timeText = rec.entry_time_display
                  } else {
                    timeText = "NT"
                  }
                }
                const ageText = swimmerAge ? String(swimmerAge) : ""
                const numberText = swimmerNumber || ""
                const clubAbbrev = formatClubAbbrev(rec.club_name)

                const displayName =
                  swimmerName && rec.is_exhibition
                    ? `ex. ${swimmerName}`
                    : swimmerName

                // Swimmer rows (individual events): slightly smaller
                // font than the summary font.
                doc.setFontSize(swimmerRowFontSize)
                doc.setFont("MuseoSansRounded-500", "normal")
                const laneWidth = doc.getTextWidth(laneStr)
                doc.text(laneStr, laneX - laneWidth, yPos)
                if (displayName) {
                  doc.text(displayName, nameX, yPos)
                }
                if (ageText) {
                  const ageWidth = doc.getTextWidth(ageText)
                  doc.text(ageText, ageX - ageWidth, yPos)
                }
                if (clubX !== null && clubAbbrev) {
                  doc.text(clubAbbrev, clubX, yPos)
                }
                if (numberText) {
                  const idHeaderWidth = doc.getTextWidth("ID")
                  const idAnchorX = numberX - idHeaderWidth
                  doc.text(numberText, idAnchorX, yPos)
                }
                if (timeText) {
                  const timeWidth = doc.getTextWidth(timeText)
                  doc.text(timeText, timeX - timeWidth, yPos)
                }
                yPos += columns === 3 ? 4 : 5
              }
            })

            // Small gap between heats.
            yPos += columns === 3 ? 2 : 3
          })
        } else {
          // Editing Entries context: psych-sheet style view where
          // heats and lanes are not yet finalized. We seed
          // individuals and relay teams by entry time and hide true
          // lane assignments.

          // Build seeded lists (no heat headers) for this event.
          const allIndividuals = individuals.slice().map((item) => {
            const rec = item.rec
            const timeStr = rec.entry_time_display || ""
            const parsed = timeStr ? parseTime(timeStr) : null
            const isEx = !!rec.is_exhibition
            return {
              ...item,
              rec,
              isExhibition: isEx,
              sortTime: parsed,
            }
          })

          const officialIndividuals = allIndividuals.filter(
            (e) => !e.isExhibition,
          )
          const exhibitionIndividuals = allIndividuals.filter(
            (e) => e.isExhibition,
          )

          const timeComparator = (a, b) => {
            const ta = a.sortTime
            const tb = b.sortTime
            if (ta === null && tb === null) return 0
            if (ta === null) return 1
            if (tb === null) return -1
            return ta - tb
          }

          officialIndividuals.sort(timeComparator)
          exhibitionIndividuals.sort(timeComparator)

          const seededIndividuals = [
            ...officialIndividuals,
            ...exhibitionIndividuals,
          ]
          const isRelayEvent = relayTeams.size > 0

          // Heights used for pagination within this event.
          const individualRowHeight = columns === 3 ? 4 : 5
          const relaySummaryHeight = columns === 3 ? 4 : 5
          const relayLegHeight = 4
          const relayGapAfterTeam = 2

          if (isRelayEvent) {
            // Seed relay teams by entry time (fastest to slowest),
            // with any exhibition relays listed after official
            // relays.
            const relayTeamsArray = Array.from(relayTeams.values()).map(
              (team) => {
                const baseRec = team.eventRec
                const timeStr = baseRec.entry_time_display || ""
                const parsed = timeStr ? parseTime(timeStr) : null
                const isEx = !!baseRec.is_exhibition
                return {
                  ...team,
                  isExhibition: isEx,
                  sortTime: parsed,
                }
              },
            )

            const officialTeams = relayTeamsArray.filter(
              (t) => !t.isExhibition,
            )
            const exhibitionTeams = relayTeamsArray.filter(
              (t) => t.isExhibition,
            )

            officialTeams.sort(timeComparator)
            exhibitionTeams.sort(timeComparator)

            const seededRelayTeams = [...officialTeams, ...exhibitionTeams]

            seededRelayTeams.forEach((team, idx) => {
              const baseRec = team.eventRec
              const laneStr = String(idx + 1)

              let timeText = ""
              if (includeTimes) {
                if (baseRec.entry_time_display) {
                  timeText = baseRec.entry_time_display
                } else {
                  timeText = "NT"
                }
              }

              const legsSorted = team.legs
                .slice()
                .sort(
                  (a, b) => (a.team_position || 0) - (b.team_position || 0),
                )

              const teamBlockHeight =
                relaySummaryHeight +
                legsSorted.length * relayLegHeight +
                relayGapAfterTeam

              // Keep each relay team (summary + all legs) together on
              // the same column/page.
              checkPageHeight(teamBlockHeight)

              const colX = margin + currentColumn * (columnWidth + 5)
              const laneXTeam = colX + laneOffset
              const nameXTeam = colX + nameOffset
              const ageXTeam = colX + ageOffset
              const clubXTeam =
                clubOffset !== null ? colX + clubOffset : null
              const numberXTeam = colX + numberOffset
              const timeXTeam = colX + timeOffset
              const spaceWidthRelayTeam = doc.getTextWidth(" ")
              const relayAgeXTeam =
                isRelayEvent && clubXTeam !== null
                  ? ageXTeam + spaceWidthRelayTeam * 12
                  : ageXTeam

              const teamClubAbbrev = formatClubAbbrev(baseRec.club_name)
              const exRelaySuffix = baseRec.is_exhibition ? " ex." : ""
              const relayLabel = teamClubAbbrev
                ? `${teamClubAbbrev} Relay${exRelaySuffix}`
                : `Relay${exRelaySuffix}`

              // Relay team summary row uses the main summary size so
              // it stands out from the swimmer rows below.
              doc.setFontSize(summaryFontSize)
              doc.setFont("MuseoSansRounded-500", "normal")
              const laneWidth = doc.getTextWidth(laneStr)
              doc.text(laneStr, laneXTeam - laneWidth, yPos)
              if (relayLabel) {
                doc.text(relayLabel, nameXTeam, yPos)
              }
              if (clubXTeam !== null && teamClubAbbrev) {
                doc.text(teamClubAbbrev, clubXTeam, yPos)
              }
              if (timeText) {
                const timeWidth = doc.getTextWidth(timeText)
                doc.text(timeText, timeXTeam - timeWidth, yPos)
              }
              yPos += relaySummaryHeight

              // Swimmer rows (relay legs): slightly smaller font.
              doc.setFontSize(swimmerRowFontSize)

              legsSorted.forEach((leg) => {
                const legLabel = getRelayLegDisplay(
                  leg.team_position,
                  baseRec.stroke,
                )
                const legLine = `${legLabel}: ${leg.name}`
                const ageText = leg.age ? String(leg.age) : ""
                const numberText = leg.swimmerNumber || ""

                doc.text(legLine, nameXTeam, yPos)
                if (ageText) {
                  // Relay Age values are left-justified under the
                  // shifted Age header position.
                  doc.text(ageText, relayAgeXTeam, yPos)
                }
                if (numberText) {
                  const idHeaderWidth = doc.getTextWidth("ID")
                  const spaceWidth = doc.getTextWidth(" ")
                  const idAnchorX = numberXTeam - idHeaderWidth + spaceWidth
                  doc.text(numberText, idAnchorX, yPos)
                }
                yPos += relayLegHeight
              })

              yPos += relayGapAfterTeam
            })
          } else {
            // Seed index acts as the "lane"/place number in this
            // psych-sheet style view for individual events.
            seededIndividuals.forEach((entry, idx) => {
              const { swimmerName, swimmerAge, swimmerNumber, rec } = entry

              // Ensure there is enough space for this swimmer row,
              // allowing breaks anywhere within the event while the
              // event header remains at the top of its first column.
              checkPageHeight(individualRowHeight)

              const colX = margin + currentColumn * (columnWidth + 5)
              const laneXRow = colX + laneOffset
              const nameXRow = colX + nameOffset
              const ageXRow = colX + ageOffset
              const clubXRow =
                clubOffset !== null ? colX + clubOffset : null
              const numberXRow = colX + numberOffset
              const timeXRow = colX + timeOffset

              const laneStr = String(idx + 1)

              let timeText = ""
              if (includeTimes) {
                if (rec.entry_time_display) {
                  timeText = rec.entry_time_display
                } else {
                  timeText = "NT"
                }
              }
              const ageText = swimmerAge ? String(swimmerAge) : ""
              const numberText = swimmerNumber || ""
              const clubAbbrev = formatClubAbbrev(rec.club_name)

              const displayName =
                swimmerName && rec.is_exhibition
                  ? `ex. ${swimmerName}`
                  : swimmerName

              doc.setFontSize(swimmerRowFontSize)
              doc.setFont("MuseoSansRounded-500", "normal")
              const laneWidth = doc.getTextWidth(laneStr)
              doc.text(laneStr, laneXRow - laneWidth, yPos)
              if (displayName) {
                doc.text(displayName, nameXRow, yPos)
              }
              if (ageText) {
                const ageWidth = doc.getTextWidth(ageText)
                doc.text(ageText, ageXRow - ageWidth, yPos)
              }
              if (clubXRow !== null && clubAbbrev) {
                doc.text(clubAbbrev, clubXRow, yPos)
              }
              if (numberText) {
                const idHeaderWidth = doc.getTextWidth("ID")
                const idAnchorX = numberXRow - idHeaderWidth
                doc.text(numberText, idAnchorX, yPos)
              }
              if (timeText) {
                const timeWidth = doc.getTextWidth(timeText)
                doc.text(timeText, timeXRow - timeWidth, yPos)
              }
              yPos += individualRowHeight
            })
          }
        }

        if (idx < sortedEventKeys.length - 1) {
          yPos += 4
        }
      })
    }
  } else if (viewMode === "swimmer") {
    // By Swimmer view: one block per swimmer, sorted alphabetically by
    // last name across ALL age groups. No age-group section headers.
    const filteredSwimmers = recordsAggregated
      .map((swimmer) => {
        const records = swimmer.records.filter((rec) => {
          if (relaysOnly && rec.is_relay !== 1) return false
          return true
        })
        if (!records.length) return null
        return { ...swimmer, records }
      })
      .filter(Boolean)
      .sort((a, b) => a.last_name.localeCompare(b.last_name))

    filteredSwimmers.forEach((swimmer) => {
      const blockHeight = computeRequiredHeightForSwimmer(
        swimmer,
        includeNotes,
        includeHeatLane,
        doc,
        columnWidth,
        includeTimes,
        isPsychSheetThreeColumn,
      )
      checkPageHeight(blockHeight)
      const x = margin + currentColumn * (columnWidth + 5)

      // Swimmer header: name, age, swimmer number
      doc.setFontSize(swimmerNameFontSize)
      doc.setFont("MuseoSansRounded-700", "normal")
      const primaryRecord = swimmer.records[0] || {}
      const swimmerName = primaryRecord.name || ""
      const swimmerAge = primaryRecord.age || ""
      const rawSwimmerNumber =
        primaryRecord.swim_numb !== undefined && primaryRecord.swim_numb !== null
          ? String(primaryRecord.swim_numb)
          : ""
      const swimmerNumber = rawSwimmerNumber
        ? rawSwimmerNumber.padStart(3, "0")
        : ""
      const swimmerHeader = `${swimmerName}${
        swimmerAge ? `, ${swimmerAge}` : ""
      }${swimmerNumber ? ` (#${swimmerNumber})` : ""}`
      doc.text(swimmerHeader, x, yPos)
      yPos += 4

      // Per-swimmer entry summary line
      doc.setFontSize(summaryFontSize)
      doc.setFont("MuseoSansRounded-500", "normal")
      const individualCount = swimmer.records.filter(
        (r) => r.is_relay === 0 && !r.is_exhibition,
      ).length
      const relayCount = swimmer.records.filter((r) => r.is_relay === 1 && !r.is_exhibition).length
      const exhibitionCount = swimmer.records.filter((r) => r.is_relay === 0 && r.is_exhibition).length
      const exhibitionRelayCount = swimmer.records.filter((r) => r.is_relay === 1 && r.is_exhibition).length
      const entriesSummary = relaysOnly
        ? `Entries \u2013 Relay: ${relayCount}`
        : `Entries \u2013 Ind: ${individualCount}  Relay: ${relayCount}  Ex. Ind: ${exhibitionCount}  Ex. Relay: ${exhibitionRelayCount}`
      doc.text(entriesSummary, x, yPos)
      // Faint divider under the summary line
      const dividerY = yPos + 0.8
      const summaryWidth = doc.getTextWidth(entriesSummary)
      const lineEndX = x + Math.min(summaryWidth + 1, columnWidth - 2)
      doc.setDrawColor(180)
      doc.setLineWidth(0.1)
      doc.line(x, dividerY, lineEndX, dividerY)
      doc.setDrawColor(0)
      yPos += 6

      // Individual event entries sorted by event number
      swimmer.records
        .slice()
        .sort((a, b) => (a.event_numb || 0) - (b.event_numb || 0))
        .forEach((rec) => {
          const genderLabel = formatGenderLabel(rec.event_gender)
          const distance = rec.distance || ""
          const course = rec.course || ""
          const stroke = rec.stroke || ""
          const ageGroup = rec.age_group || ""
          const eventNumber = resolveEvNum(rec.display_number, rec.event_numb)
          const divSuffix = rec.division ? ` [${rec.division}]` : ""
          const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
          const eventNumberFormatted = eventNumberStr
            ? eventNumberStr.padStart(2, "0")
            : ""

          const prefix = `Event ${eventNumberFormatted}${divSuffix}: `
          const mainText = `${ageGroup} ${genderLabel} ${distance}${course} ${stroke}`.trim()
          const line1 = `${prefix}${mainText}`

          doc.text(line1, x, yPos)
          yPos += 4

          const indentWidth = doc.getTextWidth(prefix)
          const xDetails = x + indentWidth

          const parts = []
          if (includeHeatLane) {
            parts.push(`H: ${rec.heat || "?"}  L: ${rec.lane || "?"}`)
          }
          if (includeTimes) {
            if (rec.entry_time_display) {
              parts.push(`Entry: ${rec.entry_time_display}`)
            } else {
              parts.push("Entry: NT")
            }
          }
          if (rec.is_relay === 1) {
            const legDisplay = getRelayLegDisplay(rec.team_position, rec.stroke)
            if (legDisplay) {
              parts.push(`Leg: ${legDisplay}`)
            }
          }
          if (rec.is_exhibition) {
            parts.push("Ex.")
          }

          if (parts.length > 0) {
            const line2 = parts.join("  ")
            doc.text(line2, xDetails, yPos)
            yPos += 4
          }
        })

      if (includeNotes) {
        yPos += 3
        doc.setFontSize(notesLabelFontSize)
        doc.setFont("MuseoSansRounded-500", "italic")
        doc.text("Swimmer notes:", x, yPos)
        yPos += 7
      } else {
        yPos += 3
      }
    })
  } else if (openOnlyMeet) {
    // Open-only means no age-group chunks; render a single
    // alphabetical list of swimmers that pass filters.
    const filteredSwimmers = recordsAggregated
      .map((swimmer) => {
        const records = swimmer.records.filter((rec) => {
          const ageGroup = rec.age_group || ""

          if (selectedAgeGroupSet.size && !selectedAgeGroupSet.has(ageGroup)) return false
          if (relaysOnly && rec.is_relay !== 1) return false
          return true
        })
        if (!records.length) return null
        return { ...swimmer, records }
      })
      .filter(Boolean)
      .sort((a, b) => a.last_name.localeCompare(b.last_name))

    filteredSwimmers.forEach((swimmer) => {
      const blockHeight = computeRequiredHeightForSwimmer(
        swimmer,
        includeNotes,
        includeHeatLane,
        doc,
        columnWidth,
        includeTimes,
        isPsychSheetThreeColumn,
      )
      checkPageHeight(blockHeight)
      const x = margin + currentColumn * (columnWidth + 5)

      // Swimmer header: Museo Sans Rounded 700 (true heavier weight)
      doc.setFontSize(swimmerNameFontSize)
      doc.setFont("MuseoSansRounded-700", "normal")
      const primaryRecord = swimmer.records[0] || {}
      const swimmerName = primaryRecord.name || ""
      const swimmerAge = primaryRecord.age || ""
      const rawSwimmerNumber =
        primaryRecord.swim_numb !== undefined && primaryRecord.swim_numb !== null
          ? String(primaryRecord.swim_numb)
          : ""
      const swimmerNumber = rawSwimmerNumber
        ? rawSwimmerNumber.padStart(3, "0")
        : ""
      const swimmerHeader = `${swimmerName}${
        swimmerAge ? `, ${swimmerAge}` : ""
      }${swimmerNumber ? ` (#${swimmerNumber})` : ""}`
      doc.text(swimmerHeader, x, yPos)
      // Tighter stacking: keep summary close to the name
      yPos += 4

      // Per-swimmer summary directly under the name
      doc.setFontSize(summaryFontSize)
      doc.setFont("MuseoSansRounded-500", "normal")
      const individualCount = swimmer.records.filter((r) => r.is_relay === 0 && !r.is_exhibition).length
      const relayCount = swimmer.records.filter((r) => r.is_relay === 1 && !r.is_exhibition).length
      const exhibitionCount = swimmer.records.filter((r) => r.is_relay === 0 && r.is_exhibition).length
      const exhibitionRelayCount = swimmer.records.filter((r) => r.is_relay === 1 && r.is_exhibition).length
      const entriesSummary = relaysOnly
        ? `Entries – Relay: ${relayCount}`
        : `Entries – Ind: ${individualCount}  Relay: ${relayCount}  Ex. Ind: ${exhibitionCount}  Ex. Relay: ${exhibitionRelayCount}`
      doc.text(entriesSummary, x, yPos)
      // Faint divider just under the swimmer's summary line,
      // matching the width of the summary text above it.
      const dividerY = yPos + 0.8
      const summaryWidth = doc.getTextWidth(entriesSummary)
      const lineEndX = x + Math.min(summaryWidth + 1, columnWidth - 2)
      doc.setDrawColor(180)
      doc.setLineWidth(0.1)
      doc.line(x, dividerY, lineEndX, dividerY)
      // Reset draw color to default (black) for subsequent content
      doc.setDrawColor(0)

      // Small gap between swimmer summary and first entry line
      yPos += 6

      swimmer.records
        .slice()
        .sort((a, b) => (a.event_numb || 0) - (b.event_numb || 0))
        .forEach((rec) => {
          const genderLabel = formatGenderLabel(rec.event_gender)
          const distance = rec.distance || ""
          const course = rec.course || ""
          const stroke = rec.stroke || ""
          const ageGroup = rec.age_group || ""
          const eventNumber = resolveEvNum(rec.display_number, rec.event_numb)
          const divSuffix = rec.division ? ` [${rec.division}]` : ""
          const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
          const eventNumberFormatted = eventNumberStr
            ? eventNumberStr.padStart(2, "0")
            : ""

          const prefix = `Event ${eventNumberFormatted}${divSuffix}: `
          const mainText = `${ageGroup} ${genderLabel} ${distance}${course} ${stroke}`.trim()
          let line1 = `${prefix}${mainText}`

          // Two-line layout: event on first line, details on a second,
          // slightly indented line. Psych vs heat sheet differ only in
          // whether Heat/Lane is included on the details line.
          doc.text(line1, x, yPos)
          yPos += 4

          const indentWidth = doc.getTextWidth(prefix)
          const xDetails = x + indentWidth

          let parts = []
          if (includeHeatLane) {
            // Compact heat/lane label to reduce width in 3-column layout
            parts.push(`H: ${rec.heat || "?"}  L: ${rec.lane || "?"}`)
          }
          if (includeTimes) {
            if (rec.entry_time_display) {
              parts.push(`Entry: ${rec.entry_time_display}`)
            } else {
              parts.push("Entry: NT")
            }
          }
          if (rec.is_relay === 1) {
            const legDisplay = getRelayLegDisplay(rec.team_position, rec.stroke)
            if (legDisplay) {
              parts.push(`Leg: ${legDisplay}`)
            }
          }
          if (rec.is_exhibition) {
            parts.push("Ex.")
          }

          if (parts.length > 0) {
            const line2 = parts.join("  ")
            doc.text(line2, xDetails, yPos)
            yPos += 4
          }
        })

      if (includeNotes) {
        yPos += 4
        // Swimmer notes label italicized in Museo Sans
        doc.setFontSize(notesLabelFontSize)
        doc.setFont("MuseoSansRounded-500", "italic")
        doc.text("Swimmer notes:", x, yPos)
        // Extra spacing after notes before next swimmer
        yPos += 9
      } else {
        yPos += 3
      }
    })
  } else {
    // Bucket keys are derived from the swimmer-level assignments already
    // computed in swimmersWithBuckets (keyed by league age vs. selected
    // age-group ranges), not from individual rec.age_group values.
    const allBucketKeys = new Set(
      swimmersWithBuckets.flatMap((s) => s.buckets),
    )

    const orderedBuckets = Array.from(allBucketKeys)
      .map((key) => {
        const [ageGroup, genderLabel] = key.split(":::")
        return { value: key, ageGroup, genderLabel }
      })
      .sort((a, b) => {
        const aKey = getAgeGroupSortKey(a.ageGroup)
        const bKey = getAgeGroupSortKey(b.ageGroup)
        if (aKey !== bKey) return aKey - bKey
        if (a.ageGroup !== b.ageGroup) {
          return String(a.ageGroup).localeCompare(String(b.ageGroup))
        }
        const order = { Girls: 0, Boys: 1, All: 2 }
        const aIdx = order[a.genderLabel] !== undefined ? order[a.genderLabel] : 3
        const bIdx = order[b.genderLabel] !== undefined ? order[b.genderLabel] : 3
        if (aIdx !== bIdx) return aIdx - bIdx
        return String(a.genderLabel).localeCompare(String(b.genderLabel))
      })

    orderedBuckets.forEach((bucket) => {
      const swimmersInBucket = swimmersWithBuckets
        .filter((swimmer) => swimmer.buckets.includes(bucket.value))
        .map((swimmer) => {
          // Show all of this swimmer's records (not just records whose
          // age_group matches the bucket label) so swim-ups and
          // overlapping age groups display every event under the swimmer's
          // section regardless of which event age group they were entered in.
          const records = swimmer.records.filter((rec) => {
            if (relaysOnly && rec.is_relay !== 1) return false
            return true
          })
          if (!records.length) return null
          return { ...swimmer, records }
        })
        .filter(Boolean)
        .sort((a, b) => a.last_name.localeCompare(b.last_name))

      if (!swimmersInBucket.length) return

      // Per-bucket summary
      let bucketIndividual = 0
      let bucketExhibition = 0
      const bucketRelayTeams = new Set()
      const bucketExhibitionRelayTeams = new Set()

      swimmersInBucket.forEach((swimmer) => {
        bucketIndividual += swimmer.records.filter((r) => r.is_relay === 0 && !r.is_exhibition).length
        bucketExhibition += swimmer.records.filter((r) => r.is_relay === 0 && r.is_exhibition).length

        swimmer.records
          .filter((r) => r.is_relay === 1)
          .forEach((r) => {
            const key = `${r.event_numb || ""}|${r.heat || ""}|${r.lane || ""}`
            if (r.is_exhibition) {
              bucketExhibitionRelayTeams.add(key)
            } else {
              bucketRelayTeams.add(key)
            }
          })
      })

      const bucketRelay = bucketRelayTeams.size
      const bucketExhibitionRelay = bucketExhibitionRelayTeams.size

      const bucketHeaderHeight = 12
      // Add generous spacing before each age-group/sex header, but
      // only when it does NOT start a new page/column (yPos === 40).
      const previousY = yPos
      checkPageHeight(bucketHeaderHeight)

      if (yPos === previousY && yPos > 40) {
        yPos += 6
      }

      const columnStartX = margin + currentColumn * (columnWidth + 5)

      // Age-group header: slightly larger, with underline across the full label
      const ageGroupText = bucket.ageGroup || ""
      const genderText = bucket.genderLabel || ""
      const bucketTitle = `${ageGroupText} ${genderText}`.trim()

      doc.setFontSize(12)
      // Age-group/sex header in Museo Sans Rounded 700
      doc.setFont("MuseoSansRounded-700", "normal")
      doc.text(bucketTitle, columnStartX, yPos)

      // Underline the entire age-group/sex header text
      if (bucketTitle) {
        const titleWidth = doc.getTextWidth(bucketTitle)
        const underlineY = yPos + 0.5
        doc.line(columnStartX, underlineY, columnStartX + titleWidth, underlineY)
      }

      // Tighter vertical stacking for the per-bucket summary
      yPos += 4

      doc.setFontSize(isThreeColumnLayout ? 7 : 8)
      doc.setFont("MuseoSansRounded-500", "normal")
      const bucketSummaryLine = relaysOnly
        ? `Summary – Relay: ${bucketRelay}`
        : `Summary – Ind: ${bucketIndividual}  Relay: ${bucketRelay}  Ex. Ind: ${bucketExhibition}  Ex. Relay: ${bucketExhibitionRelay}`
      doc.text(bucketSummaryLine, columnStartX, yPos)
      // A bit more gap before the first swimmer in this bucket
      yPos += 6

      swimmersInBucket.forEach((swimmer) => {
        const blockHeight = computeRequiredHeightForSwimmer(
          swimmer,
          includeNotes,
          includeHeatLane,
          doc,
          columnWidth,
          includeTimes,
          isPsychSheetThreeColumn,
        )
        checkPageHeight(blockHeight)
        const x = margin + currentColumn * (columnWidth + 5)

        // Swimmer header (name, age, number) in Museo Sans Rounded 700
        doc.setFontSize(swimmerNameFontSize)
        doc.setFont("MuseoSansRounded-700", "normal")
        const primaryRecord = swimmer.records[0] || {}
        const swimmerName = primaryRecord.name || ""
        const swimmerAge = primaryRecord.age || ""
        const rawSwimmerNumber =
          primaryRecord.swim_numb !== undefined && primaryRecord.swim_numb !== null
            ? String(primaryRecord.swim_numb)
            : ""
        const swimmerNumber = rawSwimmerNumber
          ? rawSwimmerNumber.padStart(3, "0")
          : ""
        const swimmerHeader = `${swimmerName}${
          swimmerAge ? `, ${swimmerAge}` : ""
        }${swimmerNumber ? ` (#${swimmerNumber})` : ""}`
        doc.text(swimmerHeader, x, yPos)
        // Tighter stacking: keep summary close to the name
        yPos += 4

        // Entries summary for this swimmer within the bucket
        doc.setFontSize(summaryFontSize)
        doc.setFont("MuseoSansRounded-500", "normal")
        const individualCount = swimmer.records.filter(
          (r) => r.is_relay === 0 && !r.is_exhibition,
        ).length
        const relayCount = swimmer.records.filter((r) => r.is_relay === 1 && !r.is_exhibition).length
        const exhibitionCount = swimmer.records.filter((r) => r.is_relay === 0 && r.is_exhibition).length
        const exhibitionRelayCount = swimmer.records.filter((r) => r.is_relay === 1 && r.is_exhibition).length
        const entriesSummary = relaysOnly
          ? `Entries – Relay: ${relayCount}`
          : `Entries – Ind: ${individualCount}  Relay: ${relayCount}  Ex. Ind: ${exhibitionCount}  Ex. Relay: ${exhibitionRelayCount}`
        doc.text(entriesSummary, x, yPos)
        // Faint divider just under the swimmer's summary line,
        // matching the width of the summary text above it.
        const dividerY = yPos + 0.8
        const summaryWidth = doc.getTextWidth(entriesSummary)
        const lineEndX = x + Math.min(summaryWidth + 1, columnWidth - 2)
        doc.setDrawColor(180)
        doc.setLineWidth(0.1)
        doc.line(x, dividerY, lineEndX, dividerY)
        doc.setDrawColor(0)

        // A little bit of spacing between the swimmer's entry summary
        // line and their first entry line
        yPos += 5

        swimmer.records
          .slice()
          .sort((a, b) => (a.event_numb || 0) - (b.event_numb || 0))
          .forEach((rec) => {
            const genderLabel = formatGenderLabel(rec.event_gender)
            const distance = rec.distance || ""
            const course = rec.course || ""
            const stroke = rec.stroke || ""
            const ageGroup = rec.age_group || ""
            const eventNumber = resolveEvNum(rec.display_number, rec.event_numb)
            const divSuffix = rec.division ? ` [${rec.division}]` : ""
            const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
            const eventNumberFormatted = eventNumberStr
              ? eventNumberStr.padStart(2, "0")
              : ""

            const prefix = `Event ${eventNumberFormatted}${divSuffix}: `
            const mainText = `${ageGroup} ${genderLabel} ${distance}${course} ${stroke}`.trim()
            let line1 = `${prefix}${mainText}`

            // Two-line layout: event on first line, details on a second,
            // slightly indented line. Psych vs heat sheet differ only in
            // whether Heat/Lane is included on the details line.
            doc.text(line1, x, yPos)

            yPos += 4

            const indentWidth = doc.getTextWidth(prefix)
            const xDetails = x + indentWidth

            let parts = []
            if (includeHeatLane) {
              // Compact heat/lane label to reduce width in 3-column layout
              parts.push(`H: ${rec.heat || "?"}  L: ${rec.lane || "?"}`)
            }
            if (includeTimes) {
              if (rec.entry_time_display) {
                parts.push(`Entry: ${rec.entry_time_display}`)
              } else {
                parts.push("Entry: NT")
              }
            }
            if (rec.is_relay === 1) {
              const legDisplay = getRelayLegDisplay(rec.team_position, rec.stroke)
              if (legDisplay) {
                parts.push(`Leg: ${legDisplay}`)
              }
            }
            if (rec.is_exhibition) {
              parts.push("Ex.")
            }

            if (parts.length > 0) {
              const line2 = parts.join("  ")
              doc.text(line2, xDetails, yPos)
              yPos += 4
            }
          })

        if (includeNotes) {
          yPos += 3
          // Swimmer notes label italicized in Museo Sans
          doc.setFontSize(notesLabelFontSize)
          doc.setFont("MuseoSansRounded-500", "italic")
          doc.text("Swimmer notes:", x, yPos)
          // Small extra spacing after notes before the next swimmer
          yPos += 7
        } else {
          yPos += 3
        }
      })
    })
  }

  const fileName = formatFileName(meetName, meetDate)

  // Add page numbers (e.g., "Page 3 of 14") to each page
  const pageCount = doc.getNumberOfPages()
  doc.setFontSize(8)
  doc.setFont("MuseoSansRounded-500", "normal")
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    const pageLabel = `Page ${i} of ${pageCount}`
    const pageWidthLocal = doc.internal.pageSize.getWidth()
    const pageHeightLocal = doc.internal.pageSize.getHeight()
    const x = pageWidthLocal - doc.getTextWidth(pageLabel) - 10
    const y = pageHeightLocal - 8
    doc.text(pageLabel, x, y)
  }

  const blobUrl = doc.output("bloburl")
  if (!options || options.mode !== "previewOnly") {
    doc.save(fileName)
  }

  return blobUrl
}
